import React, { useEffect, useState } from "react";
import { useTranslation } from "@me8eon/translation";
import "./ManageBooking.scss";
import TableComponent, {
    TableAction,
} from "../../generic-components/data-table/TableComponent";
import { getAccessToken } from "../../../services/accessToken";
import {
    BookingData,
    ManageBookingData,
    // MEETING_TYPES,
    MEETING_ROOM_TYPES,
    UPCOMING_BOOKING_DAYS,
} from "../RoomBookingInterface";
import EditBooking from "../edit-booking/EditBooking";
import CancelBooking from "../cancel-booking/CancelBooking";
import tableHeaderLabel from "./tableHeaderLabel.json";
import SearchBooking from "../search-booking/SearchBooking";
import Modal from "../../modal/Modal";
import useRoomBookingStore from "../../../store/roomBookingStore";
import { getUserBookingApi } from "../../../services/roomBooking";
import { convertDaysToMilliseconds, formatDate, getFormattedDateTime } from "../../../util/date";
import LoaderComponent from "../../loader-component/LoaderComponent";
import { useJwtToken } from "@me8eon/react_authentication";

type tableDataType = {
    [key: string]: string;
};
type tableDataLang = {
    [key: string]: tableDataType;
};

type ManageBookingProps = {
    reload: number;
};

const ManageBooking = ({reload}: ManageBookingProps) => {
    const { t, i18n } = useTranslation("room-booking");
    const [cols, setCols] = useState<string[]>([]);
    const [headerMaps, setHeaderMaps] = useState<string[]>([]);
    const [originalResData, setOriginalResData] = useState<ManageBookingData[]>(
        []
    );
    const [filteredResData, setFilteredResData] = useState<ManageBookingData[]>(
        []
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const tableHeaderData: tableDataLang = tableHeaderLabel;
    const lang = i18n.language;
    const currentTime = new Date().getTime();
    const jwtToken = useJwtToken();

    const {
        cancelBookingModal,
        setCancelBookingModal,
        editBookingModal,
        setEditBookingModal,
        setCurrentBookingData,
        setManageBookingRawData,
        setFromDate,
        setToDate,
        searchType,
        searchQuery,
        fromDate,
        toDate,
        userLocalTimezone,
        invalidToDate
    } = useRoomBookingStore();

    useEffect(() => {
        setFromDate(getFormattedDateTime(currentTime, "Y-m-d", false, userLocalTimezone));
        setToDate(getFormattedDateTime(currentTime + convertDaysToMilliseconds(UPCOMING_BOOKING_DAYS), "Y-m-d", false, userLocalTimezone));
    }, []);

    useEffect(() => {
        if (fromDate && toDate && !invalidToDate) {
            getUserBookings();
        }
    }, [fromDate, toDate, reload]);

    useEffect(() => {
        if (searchQuery.length || searchType !== "All") {
            setFilteredResData(
                originalResData.filter(
                    (meeting: ManageBookingData) =>
                        (meeting.meeting_id
                            .toString()
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                            meeting.meeting_desc
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase()) ||
                            meeting.meeting_location
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase())) &&
                        (searchType !== "All"
                            ? meeting.meeting_type.toLowerCase() ===
                              searchType.toLowerCase()
                            : true)
                )
            );
        } else {
            setFilteredResData(originalResData);
        }
    }, [searchQuery, searchType, originalResData]);

    useEffect(() => {
        if (filteredResData.length) {
            setCols(
                Object.keys(filteredResData[0]).map(
                    (key) => tableHeaderData[i18n.language][key]
                )
            );
            setHeaderMaps(Object.keys(filteredResData[0]).map((key) => key));
        }
    }, [filteredResData, lang]);

    const getUserBookings = async () => {
        setIsLoading(true);
        const actualToken = await getAccessToken(false, jwtToken);
        const currentDate = getFormattedDateTime(currentTime, "Y-m-d", false, userLocalTimezone);
        const format = "Y-m-d H:i:s";
        const payload = {
            timeframe: {
                begin: {
                    datetime: currentDate === fromDate ? getFormattedDateTime(currentTime, format, false, userLocalTimezone) : getFormattedDateTime(new Date(fromDate + " 00:00:00").getTime(), format, false, userLocalTimezone),
                    timezone_id: userLocalTimezone
                },
                end: {
                    datetime: getFormattedDateTime(new Date(toDate + " 23:59:59").getTime(), format, false, userLocalTimezone),
                    timezone_id: userLocalTimezone
                },
            },
            accesstoken: actualToken || "",
            dummytoken: "",
            include: ["booking_container", "object", "seating"],
        };

        try {
            const response = await getUserBookingApi(payload, jwtToken);
            const bookings = response.data;
            if (response.status == "success") {
                setManageBookingRawData(bookings);
                setOriginalResData(formatManageBookingData(bookings));
                setFilteredResData(formatManageBookingData(bookings));
            } else {
                console.log("something went wrong!", response);
            }
        } catch (e) {
            console.log("something went wrong!", e);
        } finally {
            setIsLoading(false);
        }
    };

    const actions: TableAction[] = [
        {
            icon: "edit",
            onClick: (data: ManageBookingData) => {
                setCurrentBookingData(data);
                setEditBookingModal(true);
            },
        },
        {
            icon: "trash_bin",
            onClick: (data: ManageBookingData) => {
                setCurrentBookingData(data);
                setCancelBookingModal(true);
            },
        },
    ];

    const formatManageBookingData = (resData: BookingData[]) => {
        const manageBookingData: ManageBookingData[] = [];
        resData.forEach((value: BookingData) => {
            manageBookingData.push({
                meeting_id: value.booking_id,
                meeting_on: formatDate(value.begin.timestamp),
                meeting_timing: `${getFormattedDateTime(
                    value.begin.timestamp * 1000,
                    "H:i",
                    true,
                    userLocalTimezone
                )} - ${getFormattedDateTime(
                    value.end.timestamp * 1000,
                    "H:i",
                    true,
                    userLocalTimezone
                )}`,
                meeting_desc: value.topic,
                meeting_location: value.object_name,
                meeting_type: MEETING_ROOM_TYPES.find(type => type.id === value.object_type_id)?.name ?? "",
                meeting_participants: value.capacity,
                actions: actions,
            });
        });

        return manageBookingData;
    };

    const reloadManageBookingList = () => {
        getUserBookings();
    };

    return (
        <div className="child-container">
            <div className="page-sub-title">{t("manageBookings")}</div>
                <div className="manage-bookings-container">
                    <SearchBooking />
            
                    { isLoading && <LoaderComponent isLoading isLinear /> }
                    { originalResData.length ? (
                    <>
                        <TableComponent
                        rows={filteredResData}
                        cols={cols}
                        headerMaps={headerMaps}
                        showPagination={false}
                        tooltipColumns={['meeting_desc','meeting_location']}
                        />
                
                        <Modal
                        toggleModal={editBookingModal}
                        closeModal={() => setEditBookingModal(!editBookingModal)}
                        isCloseIcon={false}
                        closeOnBackdrop={false}
                        >
                        <EditBooking callback={reloadManageBookingList} action="edit" />
                        </Modal>
                
                        <Modal
                        toggleModal={cancelBookingModal}
                        closeModal={() => setCancelBookingModal(!cancelBookingModal)}
                        isCloseIcon={false}
                        closeOnBackdrop={false}
                        >
                        <CancelBooking callback={reloadManageBookingList} />
                        </Modal>
                    </>
                    ) : (
                        !isLoading && <div className="no-booking-title">{t("noUpcomingBooking")}</div>
                    )}
            </div>
        </div>
    );
};

export default ManageBooking;
